// src/game/config.js
// 🎮 90s Mario-Style Wedding Game Configuration
// UPDATED: 50 items per level, 1 minute timer

export const GAME_CONFIG = {
  // Core Game Settings
  TITLE: "Pablo & Bettina's Wedding Game",
  VERSION: "1.0.0",
  
  // Display Settings (90s style resolutions)
  CANVAS: {
    WIDTH: 800,           // 4:3 aspect ratio like old games
    HEIGHT: 600,
    PIXEL_ART: true,      // Sharp pixel rendering
    ANTIALIAS: false,     // Keep pixels crisp
    BACKGROUND_COLOR: '#5C94FC' // Mario blue
  },
  
  // Mario-Style Physics
  PHYSICS: {
    GRAVITY: 800,         // Fast, snappy gravity like Mario
    PLAYER_SPEED: 160,    // Responsive movement
    JUMP_FORCE: -400,     // Strong, predictable jumps
    WORLD_SPEED: 120,     // Auto-scroll speed
    FRICTION: 0.85,       // Smooth stopping
    BOUNCE: 0.1          // Minimal bounce for tight controls
  },
  
  // Sprite Settings (32x32 for high resolution)
  SPRITES: {
    TILE_SIZE: 32,        // Base tile size
    PLAYER_SIZE: 32,      // Character sprite size
    ITEM_SIZE: 32,        // Collectible size
    PLATFORM_WIDTH: 64,   // Platform width
    PLATFORM_HEIGHT: 40,  // Platform height
    SCALE: {
      PLAYER: 2,          // Player scaling
      ITEMS: 2,           // Item scaling
      NPCS: 2,            // NPC scaling
      PLATFORMS: 1.5,     // Platform scaling
      HEART_BOX: 1        // Heart box scaling
    },
    COLORS: {
      // 90s Mario Color Palette
      MARIO_BLUE: 0x5C94FC,
      MARIO_RED: 0xFC5C5C,
      MARIO_YELLOW: 0xFCD45C,
      MARIO_GREEN: 0x5CFC5C,
      MARIO_PURPLE: 0xB45CFC,
      MARIO_ORANGE: 0xFC945C,
      
      // Wedding Theme Colors
      BRIDE_PINK: 0xFF69B4,
      GROOM_BLUE: 0x4ECDC4,
      WEDDING_GOLD: 0xFFD700,
      WEDDING_WHITE: 0xFFFEF7,
      WEDDING_BLACK: 0x2C3E50,
      
      // UI Colors (90s Windows style)
      UI_GRAY: 0xC0C0C0,
      UI_DARK_GRAY: 0x808080,
      UI_LIGHT_GRAY: 0xE0E0E0,
      UI_BLACK: 0x000000
    }
  },
  
  // Game Progression - UPDATED
  LEVELS: {
    TOTAL: 3,
    ITEMS_PER_LEVEL: 50,  // CHANGED: From 10 to 50 items
    DISTANCES: {
      1: 2000,            // Shorter levels for mobile
      2: 2500,
      3: 3000
    },
    THEMES: {
      1: 'SHOP',          // Bridal/Groom shop
      2: 'CEREMONY',      // Wedding ceremony  
      3: 'PARTY'          // Reception party
    }
  },
  
  // Spawn Timing (in milliseconds) - ADJUSTED for more items
  SPAWN: {
    PLATFORMS: 2000,      // Platform spawn rate
    ITEMS: 800,           // FASTER: Collectible spawn rate (was 1500)
    OBSTACLES: 3000,      // Obstacle spawn rate
    NPCS: 4000,           // Cat/Miles spawn rate
    POWER_UPS: 5000       // Special items
  },
  
  // Scoring System
  SCORING: {
    ITEMS: {
      COMMON: 100,        // Regular items
      RARE: 200,          // Special items
      BONUS: 500          // Perfect timing bonuses
    },
    PENALTIES: {
      OBSTACLE_HIT: 50,   // Lost points for damage
      TIME_BONUS: 10      // Bonus points per second remaining
    },
    MULTIPLIERS: {
      COMBO: 1.5,         // Combo collection bonus
      PERFECT: 2.0,       // Perfect level bonus
      SPEED_RUN: 1.25     // Fast completion bonus
    }
  },
  
  // Character Abilities (Mario-style power-ups)
  ABILITIES: {
    BRIDE: {
      NAME: 'Veil Glide',
      DURATION: 2000,     // 2 seconds
      COOLDOWN: 5000,     // 5 second cooldown
      EFFECT: 'SLOW_FALL' // Reduces gravity
    },
    GROOM: {
      NAME: 'Hat Shield',
      DURATION: 1500,     // 1.5 seconds
      COOLDOWN: 5000,     // 5 second cooldown
      EFFECT: 'INVINCIBLE' // Blocks damage
    }
  },
  
  // Mobile Controls
  MOBILE: {
    BUTTON_SIZE: 90,      // Increased from 70 to 90
    BUTTON_ALPHA: 0.8,    
    DEADZONE: 15,         
    VIBRATION: true,      
    
    // Landscape-specific settings
    LANDSCAPE: {
      MOVEMENT_SPACING: 15,     
      ACTION_SPACING: 30,       // Increased spacing
      MARGIN_RATIO: 0.03,       
      BUTTON_SCALE_FACTOR: 8    // Bigger buttons (was 10, now 8 = bigger)
    },
    
    // Button position ratios for different orientations
    POSITIONS: {
      LANDSCAPE: {
        // Movement controls (left side, vertical)
        MOVEMENT_X: 0.08,      
        MOVEMENT_BOTTOM_Y: 0.85, 
        MOVEMENT_TOP_Y: 0.60,    // More spacing
        
        // Action controls (right side, vertical)
        ACTION_X: 0.92,          
        JUMP_Y: 0.85,            
        ABILITY_Y: 0.55          // More separated
      },
      PORTRAIT: {
        // Portrait mode positions (now supported)
        MOVEMENT_X: 0.12,
        MOVEMENT_BOTTOM_Y: 0.88,
        MOVEMENT_TOP_Y: 0.75,
        ACTION_X: 0.88,
        JUMP_Y: 0.88,
        ABILITY_Y: 0.75
      }
    }
  },
  
  // Audio Settings
  AUDIO: {
    MASTER_VOLUME: 0.7,
    SFX_VOLUME: 0.8,
    MUSIC_VOLUME: 0.6,
    MUTE_ON_FOCUS_LOSS: true
  },
  
  // Performance Settings
  PERFORMANCE: {
    MAX_PARTICLES: 50,    // Limit particle effects
    CLEANUP_DISTANCE: 200, // Remove off-screen objects
    TARGET_FPS: 60,
    VSYNC: true
  },
  
  // Development & Debug
  DEBUG: {
    SHOW_FPS: false,
    SHOW_HITBOXES: false,
    SHOW_PHYSICS: false,
    CONSOLE_LOGGING: true,
    SKIP_INTRO: false
  },
  
  // API & Backend
  API: {
    BASE_URL: 'http://localhost:3001/api',
    ENDPOINTS: {
      SCORES: '/scores',
      HEALTH: '/health',
      LEADERBOARD: '/leaderboard'
    },
    TIMEOUT: 5000,        // 5 second timeout
    RETRY_ATTEMPTS: 3
  },
  
  // Wedding Guest Features
  COMPETITION: {
    MAX_NAME_LENGTH: 20,
    LEADERBOARD_SIZE: 10,
    SAVE_REPLAY: false,   // Keep it simple for now
    ACHIEVEMENTS: {
      SPEED_DEMON: 'Complete level without getting hit',
      COLLECTOR: 'Collect all items in a level',  
      CAT_LOVER: 'Interact with Nova and Linus',
      PERFECT_WEDDING: 'Complete all 3 levels with max score'
    }
  }
};

// Game State Enums (for type safety)
export const GAME_STATES = {
  LOADING: 'LOADING',
  MENU: 'MENU', 
  PLAYING: 'PLAYING',
  PAUSED: 'PAUSED',
  LEVEL_COMPLETE: 'LEVEL_COMPLETE',
  GAME_OVER: 'GAME_OVER',
  SCOREBOARD: 'SCOREBOARD'
};

export const CHARACTER_TYPES = {
  BRIDE: 'BRIDE',
  GROOM: 'GROOM'
};

export const ITEM_TYPES = {
  // Level 1 - Shopping
  BOUQUET: 'BOUQUET',
  SHOES: 'SHOES', 
  DRESS: 'DRESS',
  SUIT: 'SUIT',
  HAT: 'HAT',
  CUFFLINKS: 'CUFFLINKS',
  
  // Level 2 - Ceremony
  RINGS: 'RINGS',
  FLOWERS: 'FLOWERS',
  BIBLE: 'BIBLE',
  
  // Level 3 - Party
  CAKE: 'CAKE',
  CHAMPAGNE: 'CHAMPAGNE',
  GIFT: 'GIFT',
  
  // Special Items
  HEART: 'HEART',
  STAR: 'STAR',
  COIN: 'COIN'
};

export const OBSTACLE_TYPES = {
  FALLING_CAKE: 'FALLING_CAKE',
  RICE_SHOWER: 'RICE_SHOWER',
  FLYING_BOUQUET: 'FLYING_BOUQUET',
  CHAMPAGNE_CORK: 'CHAMPAGNE_CORK',
  WEDDING_GUEST: 'WEDDING_GUEST'
};

export const NPC_TYPES = {
  NOVA_CAT: 'NOVA_CAT',
  LINUS_CAT: 'LINUS_CAT', 
  MILES_KID: 'MILES_KID'
};

// Export default configuration
export default GAME_CONFIG;